import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useBalloonEventsData } from '../contexts/EventData';
import { useBalloonEventsMetadata, useQuery } from 'react-ui';
import { useMapBox } from './BalloonEventsMapBox';
import {
    ClickState,
    useMousePosition as usePointer,
} from '../contexts/Pointer';
import { BalloonEventModel, CategoryModel, LngLatGeolocation } from 'ts-core';
import { LngLatLike, Point } from 'mapbox-gl';
import Supercluster from 'supercluster';
import { spawn, Thread, Transfer } from 'threads';
import { BalloonEventsCanvasWorkerFunctions } from '../canvas/workerfunctions';
import BalloonEventsWorker from '../../worker?worker&inline';
import { useBalloonEventsOptions } from '../contexts/EmbedOptions';
import { Balloon } from '../canvas/actors';
import dayjs from 'dayjs';
import { useIsMobile } from "react-ui";
import { useZeppelinPosition } from '../contexts/ZeppelinPosition';
import { HOTSPOT_MAX_ZOOM, HOTSPOT_DISPLAY_MODE } from '../hooks/useMapHotspots';
import { useMapBoundingBox } from '../contexts/MapBoundingBox';

// ── Module-scope constants (never change)
const MIN_OPACITY = 0.6;
const MAX_OPACITY = 1.0;
const MIN_SATURATION = 0.35;

// When the RENDERED map is narrower than this (small embeds, the event-page
// mini-map, phones), the whole balloon (head + string) is scaled down. This is
// driven by the actual map container width, not the device — so a small embed on
// desktop downscales too, and a wide map on a tablet does not.
const SMALL_MAP_MAX_WIDTH = 1024;
// Small map: the whole balloon (head + string) is scaled down.
const SMALL_MAP_BALLOON_SCALE = 0.7;
// Small-map balloons are also drawn slightly more transparent. Kept separate from
// the size scale so opacity doesn't track balloon-size tweaks.
const SMALL_MAP_BALLOON_ALPHA_SCALE = 0.8;
// Base balloon string (tether) length in physics units (worker default is 11).
const BASE_BALLOON_LENGTH = 11;

// Cap retries so a persistent init failure can't bleed bandwidth fetching the
// font in a hot loop. Was unbounded; observed >5k requests in 4 minutes.
const MAX_CANVAS_INIT_RETRIES = 5;

// Cache the font across re-inits (and across instances). The font is a stable
// CDN asset; fetching it once per page is enough.
let cachedFontPromise: Promise<ArrayBuffer | undefined> | null = null;
function loadFontOnce(): Promise<ArrayBuffer | undefined> {
    if (cachedFontPromise) return cachedFontPromise;
    cachedFontPromise = (async () => {
        const urls = [
            'https://res.balloon-events.com/fonts/fontawesome-webfont.woff2',
            'https://maxcdn.bootstrapcdn.com/font-awesome/4.7.0/fonts/fontawesome-webfont.eot?#iefix&v=4.7.0',
        ];
        for (const url of urls) {
            try {
                const response = await fetch(url);
                if (response.ok) return await response.arrayBuffer();
            } catch {
                // try next
            }
        }
        console.log('failed to load font');
        return undefined;
    })();
    // If the fetch chain rejects for any reason we don't want a poisoned cache;
    // clear it so a future init can try again, but only after this attempt
    // settles (so we don't refetch within the same render burst).
    cachedFontPromise.catch(() => { cachedFontPromise = null; });
    return cachedFontPromise;
}

// Capacity-fill target for the clustering path: how many items (balloons +
// clusters) the screen should comfortably hold before clusters are left
// collapsed. Intentionally the no-clustering page size (NOT the 300 fetch
// amount) — the 300 only ensures the bbox is well-covered. While the visible
// count is under this, clusters are popped soonest-first into individual pins.
const DISPLAY_FILL_DESKTOP = 20;
const DISPLAY_FILL_MOBILE = 15;

// ── Zeppelin proximity perturbation constants
const PERTURBATION_THRESHOLD = 120;
const PERTURBATION_COOLDOWN_MS = 1500;
const PERTURBATION_DURATION_MS = 500;
const PERTURBATION_AMPLITUDE = 6;

// ── Pure helpers — defined at module scope so they are never recreated
function isValidCoordinate(x: number, y: number): boolean {
    return (
        isFinite(x) &&
        isFinite(y) &&
        !isNaN(x) &&
        !isNaN(y) &&
        Math.abs(x) < 1e6 &&
        Math.abs(y) < 1e6
    );
}

function hexToHsl(hex: string) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h = 0,
        s = 0,
        l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4;
        }
        h /= 6;
    }
    return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number) {
    const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    if (s === 0) {
        const v = Math.round(l * 255);
        return { r: v, g: v, b: v };
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return {
        r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        g: Math.round(hue2rgb(p, q, h) * 255),
        b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
}

function hexToRgba(hex: string, alpha: number) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha.toFixed(2)})`;
}

function scaleRgbaAlpha(rgba: string, factor: number): string {
    return rgba.replace(
        /rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/,
        (_, r, g, b, a) => `rgba(${r},${g},${b},${(parseFloat(a) * factor).toFixed(2)})`
    );
}

export default function BalloonEventsCanvas() {
    const startedWorker = useRef(false);
    const [finishedInitWorker, setFinishedInitWorker] = useState(false);
    // Unique key to force canvas recreation - prevents transferControlToOffscreen() issues
    const [canvasKey, setCanvasKey] = useState(() => Date.now());
    // Bounded retry counter — see MAX_CANVAS_INIT_RETRIES.
    const initRetriesRef = useRef(0);
    const [currentZoom, setCurrentZoom] = useState<number>(10);

    // Handle app visibility changes:
    // - Short absence (<2min): kick the worker loop to break out of throttled RAF queue
    // - Long absence (≥2min): recreate canvas/worker fully — Chrome on macOS throttles
    //   worker RAF+timers so aggressively after idle that kick alone isn't enough
    const hiddenAtRef = useRef<number | null>(null);
    useEffect(() => {
        const LONG_IDLE_MS = 2 * 60 * 1000; // 2 minutes

        const handleVisibilityChange = () => {
            if (document.hidden) {
                hiddenAtRef.current = Date.now();
                return;
            }

            const idleMs = hiddenAtRef.current !== null ? Date.now() - hiddenAtRef.current : 0;
            hiddenAtRef.current = null;

            if (idleMs >= LONG_IDLE_MS || !finishedInitWorker) {
                // Long idle or uninitialized: full recreation is the only reliable fix
                console.log(`[BalloonEventsCanvas] Long idle (${Math.round(idleMs / 1000)}s) — recreating canvas`);
                startedWorker.current = false;
                setCanvasKey(Date.now());
            } else if (balloonContainerRef.current) {
                // Short absence: kick the render loop out of the throttled queue
                console.log(`[BalloonEventsCanvas] Short idle (${Math.round(idleMs / 1000)}s) — kicking render loop`);
                balloonContainerRef.current.kick();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [finishedInitWorker]);

    const { version, creatorURL, initialSelectedEventId, selectedBalloon } = useBalloonEventsOptions();

    const {
        events,
        selectEvent,
        selectedEvent,
        deselectEvent,
        setHoveredEventId,
        hoveredEventId,
    } = useBalloonEventsData();
    const { metadata: metaData } = useBalloonEventsMetadata();
    const mapBoxInfo = useMapBox();
    const isMobile = useIsMobile();
    // Balloon downscaling is driven by the RENDERED map width (small embeds /
    // mini-map / phones) rather than the device. Falls back to the viewport
    // until the container has been measured. Boolean so it only flips when the
    // width actually crosses the threshold (cheap as an effect dependency).
    const mapBoundingBox = useMapBoundingBox();
    const smallMap = (mapBoundingBox.width || window.innerWidth) < SMALL_MAP_MAX_WIDTH;
    const { query } = useQuery();
    // Saved-search opt-in. Default: clustering OFF — render individual pins.
    const clusteringEnabled = Boolean(query?.forceMapClustering);
    const map = mapBoxInfo?.map;
    const { position: zeppelinPosition } = useZeppelinPosition();

    // Track zoom so canvas is hidden while hotspot circles are showing
    useEffect(() => {
        if (!map) return;
        const onZoom = () => setCurrentZoom(map.getZoom());
        map.on('zoomend', onZoom);
        setCurrentZoom(map.getZoom());
        return () => { map.off('zoomend', onZoom); };
    }, [map]);

    // Perturbation tracking for balloon wobble when zeppelin passes nearby
    const perturbationMapRef = useRef<Map<string, { offsetX: number; offsetY: number; startTime: number }>>(new Map());
    const perturbationCooldownRef = useRef<Map<string, number>>(new Map());

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const balloonContainerRef = useRef<BalloonEventsCanvasWorkerFunctions | null>(null);
    const workerRef = useRef<Worker | null>(null);

    const { clickState, position } = usePointer();
    const balloonEventsRef = useRef<BalloonEventModel[]>([]);

    // ── Supercluster: distance-based decluttering on the frontend ───────────
    // The backend returns up to N raw balloons (no clustering). We index them
    // by lat/lng and ask supercluster what to render at the current zoom +
    // viewport. Output is a flat list: each item is either a single balloon
    // (rendered at the event's real coords) or a cluster (rendered at the
    // actual centroid of the contained balloons — NOT a grid centroid).
    type DisplayItem =
        | { kind: 'balloon'; id: string; lat: number; lng: number; event: BalloonEventModel }
        | { kind: 'cluster'; id: string; superclusterId: number; lat: number; lng: number; count: number; topCategoryIds: string[]; bbox: { topLeft: [number, number]; bottomRight: [number, number] }; leafEventIds: string[]; soonestStart: number };
    const superclusterRef = useRef<Supercluster<{ eventId: string; categoryId?: string }> | null>(null);
    if (!superclusterRef.current) {
        // VITE_CLUSTERING_MIN_POINTS — minimum balloons within `radius` px to
        // form a cluster. Defaults to 3: pairs stay as 2 individual balloons,
        // 3+ collapse into a "+N" cluster. Set to 2 to also cluster pairs.
        const minPoints = parseInt(import.meta.env.VITE_CLUSTERING_MIN_POINTS ?? '3', 10);
        superclusterRef.current = new Supercluster<{ eventId: string; categoryId?: string }>({
            // 50 px radius — balloons within ~50 screen-pixels at the given zoom merge.
            radius: 50,
            // Above zoom 16, never cluster — show every balloon individually.
            maxZoom: 16,
            minPoints: isFinite(minPoints) && minPoints >= 2 ? minPoints : 3,
        });
    }
    const displayItemsRef = useRef<DisplayItem[]>([]);

    const emptySpaceRef = useRef<LngLatGeolocation | null>(null);

    // Fly to the pre-selected event once — restores context after back-navigation
    // from EventPage (`initialSelectedEventId`) or zooms the mini-map in to its
    // highlighted balloon (`selectedBalloon`). Fires exactly once per mount
    // (guarded), so as long as the map isn't re-mounted it won't re-zoom.
    const preselectEventId = selectedBalloon ?? initialSelectedEventId;
    const initialFlyDoneRef = useRef(false);
    useEffect(() => {
        if (initialFlyDoneRef.current || !preselectEventId || !map || !selectedEvent) return;
        if (selectedEvent.id !== preselectEventId && selectedEvent.slug !== preselectEventId) return;
        if (!selectedEvent.location?.lat || !selectedEvent.location?.lng) return;
        initialFlyDoneRef.current = true;
        map.flyTo({
            center: [selectedEvent.location.lng, selectedEvent.location.lat],
            zoom: 15,
            duration: 1200,
        });
    }, [selectedEvent, map, preselectEventId]);

    function hoverBalloon(e: string, x: number, y: number) {
        e = e || '';
        setHoveredEventId(e);
        // On mobile, don't select from hover - let the click handler do it
    }

    useEffect(() => {
        if (!balloonContainerRef.current || !position) return;
        balloonContainerRef.current.getId(position.x, position.y).then((e) => {
            hoverBalloon(e || '', position.x, position.y);
        });
    }, [position]);

    const dragRef = useRef('');

    useEffect(() => {
        if (
            clickState === ClickState.DOWN &&
            dragRef.current &&
            position &&
            balloonContainerRef.current
        ) {
            balloonContainerRef.current.drag(position.x, position.y, dragRef.current);
            console.log('dragging', dragRef.current);
        }
    }, [position, clickState]);

    // `finishedInitWorker` is a dep so the selection is (re-)applied once the
    // worker is ready — otherwise a pre-selected event (e.g. the mini-map's
    // `selectedBalloon`, set before the worker spawns) is dropped and the
    // highlight never appears (the effect wouldn't re-run on its own).
    useEffect(() => {
        if (balloonContainerRef.current) {
            balloonContainerRef.current.select(
                selectedEvent ? selectedEvent.id : null
            );
        }
    }, [selectedEvent, finishedInitWorker]);

    useEffect(() => {
        if (balloonContainerRef.current) {
            balloonContainerRef.current.hover(hoveredEventId);
        }
    }, [hoveredEventId, finishedInitWorker]);

    const movePromiseRef = useRef<Promise<void> | null>(null);
    const nextMoveRef = useRef<boolean>(false);
    const moveStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!finishedInitWorker || !map) return;

        async function updatePositions() {
            if (balloonContainerRef.current && !movePromiseRef.current && map) {
                // Project the current supercluster output (mix of clusters and
                // balloons). Same iteration handles both — they all become
                // {id, position} entries that the worker maps to existing actors
                // by id and smoothly tweens to the new screen positions.
                const positions = displayItemsRef.current
                    .map((item) => {
                        if (!isFinite(item.lat) || !isFinite(item.lng) ||
                            Math.abs(item.lat) > 90 || Math.abs(item.lng) > 180) {
                            return null;
                        }
                        const point = map.project([item.lng, item.lat] as LngLatLike);
                        if (!isValidCoordinate(point.x, point.y)) return null;
                        if (item.kind === 'balloon') {
                            item.event.setScreenCoordinates(point.x, point.y);
                        }
                        return { id: item.id, position: { x: point.x, y: point.y } };
                    })
                    .filter((p): p is NonNullable<typeof p> => p !== null);

                movePromiseRef.current = balloonContainerRef.current.move(positions);

                // Deadlock recovery: if worker doesn't respond within 500ms,
                // clear the stale promise so new moves can proceed
                moveStallTimerRef.current = setTimeout(() => {
                    if (movePromiseRef.current) {
                        console.warn('[BalloonEventsCanvas] Move promise stalled, clearing to unblock updates');
                        movePromiseRef.current = null;
                        if (nextMoveRef.current) {
                            nextMoveRef.current = false;
                            updatePositions();
                        }
                    }
                }, 500);

                await movePromiseRef.current;
                if (moveStallTimerRef.current) {
                    clearTimeout(moveStallTimerRef.current);
                    moveStallTimerRef.current = null;
                }
                movePromiseRef.current = null;
                if (nextMoveRef.current) {
                    nextMoveRef.current = false;
                    updatePositions();
                }
            } else {
                nextMoveRef.current = true;
            }
        }

        const handleMapMove = () => {
            try {
                updatePositions();
            } catch (e) {
                console.error(e);
            }
        };

        // Listen to the map's move event
        if (map) {
            map.on('move', handleMapMove);
            updatePositions();
        }
        return () => {
            if (map) {
                map.off('move', handleMapMove);
            }
        };
    }, [finishedInitWorker, map]);

    // Zeppelin proximity perturbation effect
    useEffect(() => {
        if (!finishedInitWorker || !map || !zeppelinPosition) return;

        let animationFrameId: number;
        let lastUpdateTime = 0;

        const checkPerturbations = () => {
            const now = Date.now();

            // Throttle to ~30fps
            if (now - lastUpdateTime < 33) {
                animationFrameId = requestAnimationFrame(checkPerturbations);
                return;
            }
            lastUpdateTime = now;

            const perturbationMap = perturbationMapRef.current;
            const cooldownMap = perturbationCooldownRef.current;
            let needsUpdate = false;

            // Check each balloon for proximity to zeppelin
            for (const event of balloonEventsRef.current) {
                if (event.x == null || event.y == null) continue;

                const dx = event.x - zeppelinPosition.x;
                const dy = event.y - zeppelinPosition.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                // Check if balloon is close to zeppelin and not on cooldown
                const lastTriggered = cooldownMap.get(event.id) ?? 0;
                if (distance < PERTURBATION_THRESHOLD && now - lastTriggered > PERTURBATION_COOLDOWN_MS) {
                    cooldownMap.set(event.id, now);
                    perturbationMap.set(event.id, {
                        offsetX: (Math.random() - 0.5) * PERTURBATION_AMPLITUDE * 2,
                        offsetY: (Math.random() - 0.5) * PERTURBATION_AMPLITUDE * 2,
                        startTime: now,
                    });
                    needsUpdate = true;
                }
            }

            // Update existing perturbations (decay over time)
            for (const [eventId, pert] of perturbationMap.entries()) {
                const elapsed = now - pert.startTime;
                if (elapsed >= PERTURBATION_DURATION_MS) {
                    perturbationMap.delete(eventId);
                    needsUpdate = true;
                }
            }

            // If perturbations changed, trigger a position update
            if (needsUpdate && balloonContainerRef.current && !movePromiseRef.current) {
                const positions = balloonEventsRef.current
                    .map((e) => {
                        if (e.x == null || e.y == null) return null;

                        let x = e.x;
                        let y = e.y;

                        // Apply perturbation with easing
                        const pert = perturbationMap.get(e.id);
                        if (pert) {
                            const elapsed = now - pert.startTime;
                            const progress = Math.min(1, elapsed / PERTURBATION_DURATION_MS);
                            // Ease out cubic
                            const easeOut = 1 - Math.pow(1 - progress, 3);
                            // Apply offset that decays to zero
                            const decay = 1 - easeOut;
                            x += pert.offsetX * decay;
                            y += pert.offsetY * decay;
                        }

                        return { id: e.id, position: { x, y } };
                    })
                    .filter((p): p is NonNullable<typeof p> => p !== null);

                if (positions.length > 0) {
                    balloonContainerRef.current.move(positions);
                }
            }

            animationFrameId = requestAnimationFrame(checkPerturbations);
        };

        animationFrameId = requestAnimationFrame(checkPerturbations);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [finishedInitWorker, map, zeppelinPosition]);

    const updateBalloonDataPromiseRef = useRef<Promise<void> | null>(null);
    const nextUpdateBalloonDataRef = useRef<boolean>(false);
    // Deadlock-recovery timer for the worker's updateBalloonData frame promise,
    // mirroring the move() path. Without it, a never-resolving worker promise
    // (render loop throttled/stopped on tab backgrounding or GPU context loss)
    // wedges updateBalloonDataPromiseRef forever — every later redraw then
    // early-returns and the canvas stays frozen on whatever was last drawn,
    // which is the empty mid-transition frame when a clustering tag is removed.
    const updateBalloonDataStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Always points at the most recent display-effect's updateBalloonData. The
    // deferred re-run and the stall recovery invoke THIS, not the closure that
    // happens to own the resolving promise — after a clustering toggle the older
    // closure still has the stale clusteringEnabled/events and would repaint the
    // wrong (or empty) set with no later trigger to heal it.
    const latestUpdateBalloonDataRef = useRef<() => void>(() => {});

    // soonest & furthest future start-date
    const [minFutureDate, maxFutureDate] = useMemo(() => {
        const now = dayjs();
        const futureDates = events
            .map((ev) => dayjs(ev.latestDate.start))
            .filter((d) => d.isAfter(now));

        if (futureDates.length === 0) return [null, null];

        const min = futureDates.reduce((a, b) => (a.isBefore(b) ? a : b));
        const max = futureDates.reduce((a, b) => (a.isAfter(b) ? a : b));
        return [min, max];
    }, [events]);

    // absolute width (days) of that window
    const futureRangeDays = useMemo(() => {
        if (!minFutureDate || !maxFutureDate) return 0;
        return maxFutureDate.diff(minFutureDate, 'days');
    }, [minFutureDate, maxFutureDate]);

    /* ── getColor: memoized; only rebuilds when the future-date window changes */
    const getColor = useCallback((e: BalloonEventModel, category: CategoryModel) => {
        const PAST_COLOR = 'rgba(0,0,0,0.5)';
        const DEFAULT_HEX = '#cc128c';

        const eventDate = dayjs(e.latestDate.start);
        const now = dayjs();

        // Ongoing events (started but not yet ended) get category color at full opacity
        if (eventDate.isBefore(now) && e.timeUntilStart === 0) {
            const baseHex = category?.color ?? DEFAULT_HEX;
            return hexToRgba(baseHex, MAX_OPACITY);
        }

        if (eventDate.isBefore(now)) return PAST_COLOR;

        const baseHex = category?.color ?? DEFAULT_HEX;

        if (!minFutureDate || futureRangeDays === 0) {
            return hexToRgba(baseHex, MAX_OPACITY);
        }

        const { h, s: baseS, l } = hexToHsl(baseHex);
        const t = eventDate.diff(minFutureDate, 'days') / futureRangeDays;
        const saturation = baseS - t * (baseS - MIN_SATURATION);
        const opacity = MAX_OPACITY - t * (MAX_OPACITY - MIN_OPACITY);
        const { r, g, b } = hslToRgb(h, saturation, l);

        return `rgba(${r},${g},${b},${opacity.toFixed(2)})`;
    }, [minFutureDate, futureRangeDays]);

    // ── 1) Load events into the supercluster index whenever they change ─────
    // Skipped entirely when clustering is OFF (default). The displayItems path
    // below renders one balloon per event without consulting supercluster.
    useEffect(() => {
        if (!clusteringEnabled) return;
        const idx = superclusterRef.current;
        if (!idx) return;

        const features = events
            .filter((e) => {
                const lat = e.location.lat;
                const lng = e.location.lng;
                return (
                    lat != null && lng != null &&
                    isFinite(lat) && isFinite(lng) &&
                    Math.abs(lat) <= 90 && Math.abs(lng) <= 180
                );
            })
            .map((e) => ({
                type: 'Feature' as const,
                geometry: {
                    type: 'Point' as const,
                    coordinates: [e.location.lng, e.location.lat] as [number, number],
                },
                properties: {
                    eventId: e.id,
                    categoryId: e.category?.id,
                },
            }));

        idx.load(features);
    }, [events, clusteringEnabled]);

    // ── 2) Compute display items (balloons + clusters) from supercluster ─────
    // Re-runs on events change AND on map move/zoom (because cluster output
    // depends on bbox + zoom). The output drives BOTH the diff/upsert flow
    // (updateBalloonData) and the per-frame projection flow (move()).
    useEffect(() => {
        if (!finishedInitWorker || !map) return;

        const mapInstance = map;
        const eventById = new Map<string, BalloonEventModel>(events.map((e) => [e.id, e]));

        const computeDisplayItems = (): DisplayItem[] => {
            // Default path: clustering OFF — emit one balloon per event, skip supercluster.
            if (!clusteringEnabled) {
                const items: DisplayItem[] = [];
                for (const ev of events) {
                    const lat = ev.location?.lat;
                    const lng = ev.location?.lng;
                    if (lat == null || lng == null) continue;
                    if (!isFinite(lat) || !isFinite(lng)) continue;
                    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
                    items.push({ kind: 'balloon', id: ev.id, lat, lng, event: ev });
                }
                return items;
            }

            const idx = superclusterRef.current;
            if (!idx) return [];
            const bounds = mapInstance.getBounds();
            if (!bounds) return [];
            const bbox: [number, number, number, number] = [
                bounds.getWest(),
                bounds.getSouth(),
                bounds.getEast(),
                bounds.getNorth(),
            ];
            const zoom = Math.floor(mapInstance.getZoom());

            const features = idx.getClusters(bbox, zoom);
            const out: DisplayItem[] = [];
            for (const f of features) {
                const [lng, lat] = f.geometry.coordinates as [number, number];
                if (f.properties && (f.properties as any).cluster) {
                    const clusterId = (f.id ?? f.properties.cluster_id) as number;
                    const count = (f.properties as any).point_count as number;

                    // Top-3 distinct category ids by leaf order (proxy for "frequency").
                    let topCategoryIds: string[] = [];
                    let bboxNW: [number, number] = [lat, lng];
                    let bboxSE: [number, number] = [lat, lng];
                    // Stable id: built from the SORTED set of leaf event ids, so a
                    // cluster with the same membership gets the same id even after
                    // idx.load() reassigns supercluster's internal numeric ids.
                    // Without this, every search-response refresh respawns cluster
                    // actors and the user sees the elastic-entry physics replay.
                    let leafIds: string[] = [];
                    // Soonest (closest-to-now) start among the cluster's members, used
                    // to decide pop order in the capacity-fill below. Ongoing/past
                    // events (timeUntilStart == null) count as "now" (0).
                    let soonestStart = Number.MAX_VALUE;
                    try {
                        const leaves = idx.getLeaves(clusterId, Infinity);
                        const seen = new Set<string>();
                        let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
                        for (const leaf of leaves) {
                            const eid = (leaf.properties as any).eventId as string | undefined;
                            if (eid) {
                                leafIds.push(eid);
                                const lev = eventById.get(eid);
                                if (lev) {
                                    const t = lev.timeUntilStart == null ? 0 : lev.timeUntilStart;
                                    if (t < soonestStart) soonestStart = t;
                                }
                            }
                            const cid = (leaf.properties as any).categoryId as string | undefined;
                            if (cid && !seen.has(cid)) { seen.add(cid); topCategoryIds.push(cid); }
                            const [llng, llat] = leaf.geometry.coordinates as [number, number];
                            if (llat < minLat) minLat = llat;
                            if (llat > maxLat) maxLat = llat;
                            if (llng < minLng) minLng = llng;
                            if (llng > maxLng) maxLng = llng;
                        }
                        // Demote the "other" category (slug = 'other') so it never
                        // takes the primary slot — even if it's the dominant
                        // category in the cluster. ANY real category outranks it
                        // for the splash color; "other" only appears as primary
                        // when it's the *only* category in the cluster (in which
                        // case no demotion is possible — sort is a no-op).
                        // Stable sort preserves encounter order within each group.
                        const isOther = (catId: string) =>
                            metaData?.getCategoryById?.(catId)?.slug === 'other';
                        topCategoryIds.sort((a, b) =>
                            (isOther(a) ? 1 : 0) - (isOther(b) ? 1 : 0)
                        );
                        topCategoryIds = topCategoryIds.slice(0, 3);
                        leafIds.sort();
                        bboxNW = [maxLat, minLng];
                        bboxSE = [minLat, maxLng];
                    } catch (e) { /* leaves API can throw on stale ids; ignore */ }

                    // 8-char hash of the sorted leaf ids — stable for same membership.
                    let h = 5381;
                    const joined = leafIds.join('|');
                    for (let i = 0; i < joined.length; i++) {
                        h = ((h << 5) + h + joined.charCodeAt(i)) | 0;
                    }
                    const stableId = `cluster-${(h >>> 0).toString(36)}`;

                    out.push({
                        kind: 'cluster',
                        id: stableId,
                        superclusterId: clusterId,
                        lat, lng, count, topCategoryIds,
                        bbox: { topLeft: bboxNW, bottomRight: bboxSE },
                        leafEventIds: leafIds,
                        soonestStart,
                    });
                } else {
                    const eventId = (f.properties as any).eventId as string;
                    const ev = eventById.get(eventId);
                    if (ev) out.push({ kind: 'balloon', id: eventId, lat, lng, event: ev });
                }
            }

            // Two-state rendering rule when clustering is on:
            //   - if the total event count in the viewport ≤ fillTarget,
            //     expand every cluster into its individual balloons (the user
            //     can see each event);
            //   - otherwise return the supercluster output as-is — every
            //     dense area stays a single "+N" cluster icon, no pops. This
            //     prevents the "ugly mosaic" of a giant un-popped cluster
            //     sitting next to hundreds of individual balloons that
            //     happened to overshoot any capacity-fill threshold.
            const fillTarget = isMobile ? DISPLAY_FILL_MOBILE : DISPLAY_FILL_DESKTOP;
            const totalEvents = out.reduce(
                (sum, it) => sum + (it.kind === 'cluster' ? it.count : 1),
                0,
            );

            if (totalEvents > fillTarget) {
                return out;
            }

            // ≤ fillTarget: pop every cluster into its members.
            const result: DisplayItem[] = [];
            for (const it of out) {
                if (it.kind === 'balloon') {
                    result.push(it);
                    continue;
                }
                const members = it.leafEventIds
                    .map((id) => eventById.get(id))
                    .filter((ev): ev is BalloonEventModel => ev != null);
                let pushed = 0;
                for (const ev of members) {
                    const lat = ev.location?.lat;
                    const lng = ev.location?.lng;
                    if (lat == null || lng == null || !isFinite(lat) || !isFinite(lng)) continue;
                    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
                    result.push({ kind: 'balloon', id: ev.id, lat, lng, event: ev });
                    pushed++;
                }
                // Nothing renderable to pop into — keep the cluster icon.
                if (pushed === 0) result.push(it);
            }
            return result;
        };

        async function updateBalloonData() {
            if (updateBalloonDataPromiseRef.current || !balloonContainerRef.current) {
                nextUpdateBalloonDataRef.current = true;
                return;
            }

            const items = computeDisplayItems();
            displayItemsRef.current = items;

            // Tether REST length controls tautness, not the visible length: keep it short on a
            // small map so the buoyancy over-stretches the tether into a straight, taut line.
            // A longer rest length leaves slack and the string sags/curves.
            const balloonLength = smallMap ? 2 : BASE_BALLOON_LENGTH;
            // The VISIBLE string length is the balloon's equilibrium float height, set by the
            // upward buoyancy on the head. Scale that buoyancy down on a small map so the string
            // is shorter (in proportion with the smaller head).
            const balloonGravityScale = smallMap ? SMALL_MAP_BALLOON_SCALE * 0.8 : 1;

            const balloonData = items
                .map((item) => {
                    const point = mapInstance.project([item.lng, item.lat] as LngLatLike);
                    if (!isValidCoordinate(point.x, point.y)) return null;

                    if (item.kind === 'cluster') {
                        const colors = item.topCategoryIds
                            .map((id) => metaData?.getCategoryById?.(id)?.color)
                            .filter((c): c is string => Boolean(c))
                            .map((hex) => hexToRgba(hex, 1));
                        const baseDiameter = 50 + Math.min(40, Math.log10(Math.max(1, item.count)) * 14);
                        const diameter = smallMap ? baseDiameter * SMALL_MAP_BALLOON_SCALE : baseDiameter;
                        return {
                            id: item.id,
                            diameter,
                            length: balloonLength,
                            gravityScale: balloonGravityScale,
                            color: colors[0] ?? 'rgba(118, 134, 150, 1)',
                            colors: colors.length ? colors : ['rgba(118, 134, 150, 1)'],
                            count: item.count,
                            icon: '',
                            logoUrl: null,
                            position: { id: item.id, x: point.x, y: point.y },
                            sessions: 1,
                            ongoing: false,
                            bbox: item.bbox,
                        };
                    }

                    // Single balloon
                    const e = item.event;
                    if (e.balloonDiameterFactor <= 0 || isNaN(e.balloonDiameterFactor) || !isFinite(e.balloonDiameterFactor)) {
                        return null;
                    }
                    const category = e.category;
                    e.setScreenCoordinates(point.x, point.y);
                    const baseDiameter = 50 * e.balloonDiameterFactor;
                    const diameter = smallMap ? baseDiameter * SMALL_MAP_BALLOON_SCALE : baseDiameter;
                    if (!isFinite(diameter) || diameter <= 0 || diameter > 1000) return null;

                    const baseColor = getColor(e, category);
                    const color = smallMap ? scaleRgbaAlpha(baseColor, SMALL_MAP_BALLOON_ALPHA_SCALE) : baseColor;
                    return {
                        id: e.id,
                        color,
                        diameter,
                        length: balloonLength,
                        gravityScale: balloonGravityScale,
                        icon: category ? category.icon : '',
                        logoUrl: e.logo,
                        position: { id: e.id, x: point.x, y: point.y },
                        sessions: 1,
                        ongoing: e.timeUntilStart === 0,
                    };
                })
                .filter((b): b is NonNullable<typeof b> => b !== null);

            const thisPromise = balloonContainerRef.current.updateBalloonData(balloonData);
            updateBalloonDataPromiseRef.current = thisPromise;

            // Deadlock recovery: if the worker doesn't resolve the frame promise
            // within 500ms (throttled/stopped render loop), clear the stale
            // promise so subsequent redraws can proceed instead of being wedged.
            // Local handles + identity checks below so a late-resolving stalled
            // run can't clobber the promise/timer of a newer run.
            const stallTimer = setTimeout(() => {
                if (updateBalloonDataPromiseRef.current === thisPromise) {
                    console.warn('[BalloonEventsCanvas] updateBalloonData promise stalled, clearing to unblock redraws');
                    updateBalloonDataPromiseRef.current = null;
                    if (nextUpdateBalloonDataRef.current) {
                        nextUpdateBalloonDataRef.current = false;
                        latestUpdateBalloonDataRef.current();
                    }
                }
            }, 500);
            updateBalloonDataStallTimerRef.current = stallTimer;

            await thisPromise;
            clearTimeout(stallTimer);
            if (updateBalloonDataStallTimerRef.current === stallTimer) {
                updateBalloonDataStallTimerRef.current = null;
            }
            balloonEventsRef.current = events;
            if (updateBalloonDataPromiseRef.current === thisPromise) {
                updateBalloonDataPromiseRef.current = null;
            }
            if (nextUpdateBalloonDataRef.current) {
                nextUpdateBalloonDataRef.current = false;
                latestUpdateBalloonDataRef.current();
            }
        }

        latestUpdateBalloonDataRef.current = updateBalloonData;

        // Two complementary triggers, both safe thanks to stable cluster IDs:
        //   1. `events` change (new search response) — handled by this effect's
        //      deps re-running, which calls updateBalloonData immediately below.
        //   2. Map move/zoom WITHOUT a refetch — happens when the user zooms in
        //      or pans within the prefetched bbox (BalloonEventsMapBox skips
        //      the fetch in that case). We need to re-cluster the existing
        //      event set against the new viewport bbox + zoom, otherwise the
        //      visible cluster split stays frozen at the previous zoom.
        // When BOTH fire (rare — only when a refetch's response arrives within
        // the moveend debounce window), stable IDs make the second call a
        // smooth alter, not a respawn.
        updateBalloonData();

        let moveDebounce: ReturnType<typeof setTimeout> | null = null;
        const onMoveEnd = () => {
            if (moveDebounce) clearTimeout(moveDebounce);
            moveDebounce = setTimeout(() => updateBalloonData(), 200);
        };
        mapInstance.on('moveend', onMoveEnd);
        return () => {
            mapInstance.off('moveend', onMoveEnd);
            if (moveDebounce) clearTimeout(moveDebounce);
            if (updateBalloonDataStallTimerRef.current) {
                clearTimeout(updateBalloonDataStallTimerRef.current);
                updateBalloonDataStallTimerRef.current = null;
            }
        };
    }, [events, finishedInitWorker, map, isMobile, smallMap, metaData, getColor, clusteringEnabled]);

    useEffect(() => {
        if (!canvasRef.current) return;

        const observer = new ResizeObserver(() => {
            if (balloonContainerRef.current && canvasRef.current) {
                balloonContainerRef.current.updateDom(
                    canvasRef.current.scrollWidth,
                    canvasRef.current.scrollHeight,
                    window.devicePixelRatio
                );
            }
        });

        observer.observe(canvasRef.current);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!balloonContainerRef.current || !position) return;

        if (clickState === ClickState.UP_NOW) {
            balloonContainerRef.current.getId(position.x, position.y).then((e) => {
                if (e) {
                    // Cluster ids start with "cluster-" (synthesized by supercluster).
                    // Use supercluster.getClusterExpansionZoom() to get the zoom
                    // level at which the cluster naturally splits.
                    //
                    // Three flavors of cluster, each handled here:
                    //   (a) Normal cluster: expansionZoom < SC_MAX_ZOOM (16) →
                    //       flyTo expansionZoom + 0.5. Cluster splits on the new view.
                    //   (b) Identical-coord cluster (events sharing exact lat/lng):
                    //       expansionZoom is supercluster's maxZoom + 1, meaning
                    //       the cluster never splits via expansion. Fly *past* SC's
                    //       maxZoom so supercluster stops clustering and the events
                    //       render as overlapping individual balloons.
                    //   (c) Drill-down sanity: never zoom OUT or stay put when the
                    //       user clicked to drill in. Always advance by ≥1 zoom.
                    const item = displayItemsRef.current.find((it) => it.id === e);
                    if (item && item.kind === 'cluster' && map) {
                        const idx = superclusterRef.current;
                        const SC_MAX_ZOOM = 16;
                        const currentZoom = map.getZoom();
                        let targetZoom = currentZoom + 2;
                        if (idx) {
                            try {
                                const expansion = idx.getClusterExpansionZoom(item.superclusterId);
                                targetZoom = expansion >= SC_MAX_ZOOM
                                    ? SC_MAX_ZOOM + 1   // (b) decloak identical-coord cluster
                                    : expansion + 0.5;  // (a) split normal cluster
                            } catch (e) { /* fall back to currentZoom + 2 */ }
                        }
                        // (c) Always drill IN by at least 1 zoom level — clicking a
                        //     cluster while already past its expansion zoom should
                        //     still feel like progress, not a no-op.
                        targetZoom = Math.max(targetZoom, currentZoom + 1);
                        // Hard cap so we never exceed mapbox's effective tile depth.
                        targetZoom = Math.min(targetZoom, 18);
                        map.flyTo({ center: [item.lng, item.lat], zoom: targetZoom, duration: 600 });
                        emptySpaceRef.current = null;
                        return;
                    }

                    // Real balloon — existing select flow.
                    // On mobile, first click should select, second click should open.
                    // On desktop, click always selects (hover already selected it visually).
                    selectEvent(e, position.x, position.y, isMobile);
                    emptySpaceRef.current = null;
                } else {
                    deselectEvent();
                    if (map) {
                        const point = new Point(position.x, position.y);
                        emptySpaceRef.current = map.unproject(point) as LngLatGeolocation;
                    }
                }
            });
            if (dragRef.current)
                balloonContainerRef.current.stopDragging(dragRef.current);
        } else if (clickState === ClickState.DOWN_NOW) {
            if (hoveredEventId != '') {
                balloonContainerRef.current
                    .getBalloon(hoveredEventId)
                    .then((b: Balloon | null | undefined) => {
                        if (b && balloonContainerRef.current) {
                            dragRef.current = hoveredEventId;
                            balloonContainerRef.current.startDragging(
                                b.balloonCenter,
                                position.x,
                                position.y,
                                hoveredEventId
                            );
                        }
                    });
            }
        }
    }, [clickState]);

    // Returns true if a retry was scheduled, false if the cap was hit and the
    // caller should give up. Centralises every "force canvas recreation" branch
    // so we can never bypass the cap by adding a new call site later.
    function scheduleCanvasRetry(reason: string): boolean {
        if (initRetriesRef.current >= MAX_CANVAS_INIT_RETRIES) {
            console.error(
                `[BalloonEventsCanvas] Giving up after ${MAX_CANVAS_INIT_RETRIES} init attempts (${reason}). ` +
                'Canvas will not render. Reload the page to retry.'
            );
            return false;
        }
        initRetriesRef.current += 1;
        startedWorker.current = false;
        setCanvasKey(Date.now());
        return true;
    }

    useEffect(() => {
        if (!map || !canvasRef.current) {
            console.log(
                `[BalloonEventsCanvas] Waiting - canvas null = ${canvasRef.current == null} map null = ${map == null}`
            );
            return;
        }

        // Prevent double-initialization
        if (startedWorker.current) {
            console.log('[BalloonEventsCanvas] Worker init already in progress, skipping');
            return;
        }

        if (balloonContainerRef.current) {
            console.log('[BalloonEventsCanvas] Worker already exists, skipping');
            return;
        }

        // Set flag IMMEDIATELY before async work to prevent race conditions
        startedWorker.current = true;
        console.log('[BalloonEventsCanvas] Starting worker initialization... canvasKey:', canvasKey);

        async function init() {
            console.log('init worker');

            if (!canvasRef.current) return;

            const fontArrayBuffer = await loadFontOnce();

            console.log('[BalloonEventsCanvas] Initializing worker...');

            try {
                // Worker is bundled inline as a base64 blob (via Vite's ?worker&inline
                // suffix). Loading it produces a same-origin blob: URL, which sidesteps
                // the cross-origin Worker SecurityError when the bundle is served from
                // a CDN onto a customer page.
                console.log('[BalloonEventsCanvas] Creating worker instance...');
                workerRef.current = new BalloonEventsWorker();

                // Add error event listener
                workerRef.current.onerror = (error) => {
                    console.error('[BalloonEventsCanvas] Worker error:', error);
                    throw new Error(`Worker error: ${error.message}`);
                };

                console.log('[BalloonEventsCanvas] Spawning worker with threads.js...');
                // Let threads.js handle all worker communication protocol
                balloonContainerRef.current =
                    await spawn<BalloonEventsCanvasWorkerFunctions>(workerRef.current);
                console.log('[BalloonEventsCanvas] Worker spawned successfully');
            } catch (error) {
                console.error(
                    '[BalloonEventsCanvas] Failed to initialize worker:',
                    error
                );
                throw error; // Re-throw to be caught by the parent component
            }

            const canvas = canvasRef.current;

            // Check canvas dimensions - on Android they might be 0 initially
            const width = canvas.scrollWidth || canvas.clientWidth || canvas.offsetWidth;
            const height = canvas.scrollHeight || canvas.clientHeight || canvas.offsetHeight;

            if (width === 0 || height === 0) {
                console.warn('[BalloonEventsCanvas] Canvas has zero dimensions, retrying...', { width, height });
                // Retry after a short delay (capped via scheduleCanvasRetry)
                setTimeout(() => scheduleCanvasRetry('zero canvas dimensions'), 100);
                startedWorker.current = false;
                return;
            }

            console.log('[BalloonEventsCanvas] Canvas dimensions:', { width, height, pixelRatio: window.devicePixelRatio });

            // Check OffscreenCanvas support
            if (typeof canvas.transferControlToOffscreen !== 'function') {
                console.error('[BalloonEventsCanvas] OffscreenCanvas not supported on this device');
                return;
            }

            // transferControlToOffscreen() can only be called once per canvas element
            // If this fails, the canvas was already transferred (stale element)
            let offscreenCanvas: OffscreenCanvas;
            try {
                offscreenCanvas = canvas.transferControlToOffscreen();
                console.log('[BalloonEventsCanvas] Canvas transferred to offscreen successfully');
            } catch (transferError) {
                console.error('[BalloonEventsCanvas] Failed to transfer canvas - likely already transferred:', transferError);
                scheduleCanvasRetry('transferControlToOffscreen failed');
                return;
            }

            balloonContainerRef.current.init(
                // @ts-ignore - Transfer wrapper for threads.js worker communication
                Transfer(offscreenCanvas),
                width,
                height,
                window.devicePixelRatio,
                fontArrayBuffer
            );
            setFinishedInitWorker(true);
            console.log('[BalloonEventsCanvas] Worker initialized successfully');
            workerRef.current?.addEventListener('message', (m) => {
                if (
                    (m as MessageEvent).data &&
                    (m as MessageEvent).data.toString().includes('debug')
                ) {
                    console.log((m as MessageEvent).data);
                }
            });
        }

        init().then(() => {
            // Successful init resets the budget so transient runtime errors
            // later in the session can still trigger a recovery.
            initRetriesRef.current = 0;
        }).catch((e) => {
            console.error('[BalloonEventsCanvas] Worker initialization failed:', e);
            scheduleCanvasRetry('init() threw');
        });

        return () => {
            console.log('[BalloonEventsCanvas] Cleanup called');
            if (moveStallTimerRef.current) {
                clearTimeout(moveStallTimerRef.current);
                moveStallTimerRef.current = null;
            }
            if (balloonContainerRef.current) {
                try {
                    balloonContainerRef.current.cleanup();
                    Thread.terminate(balloonContainerRef.current as any);
                } catch (e) {
                    console.warn('[BalloonEventsCanvas] Error during worker cleanup:', e);
                }
                balloonContainerRef.current = null;
            }
            if (workerRef.current) {
                try {
                    workerRef.current.terminate();
                } catch (e) {
                    console.warn('[BalloonEventsCanvas] Error terminating worker:', e);
                }
                workerRef.current = null;
            }
            startedWorker.current = false;
            setFinishedInitWorker(false);
        };
    }, [map, canvasKey]);

    return (
        <div>
            <div
                style={{
                    top: 0,
                    left: 0,
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    //cursor: "pointer",
                    pointerEvents: 'none', //hoveredBalloon ? "auto" : "none",
                    WebkitTapHighlightColor: 'transparent',
                    visibility: HOTSPOT_DISPLAY_MODE === 'dots' || currentZoom >= HOTSPOT_MAX_ZOOM ? 'visible' : 'hidden',
                }}
                ref={containerRef}
            >
                <canvas
                    key={canvasKey}
                    style={{
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                    }}
                    ref={canvasRef}
                />
            </div>
        </div>
    );
}
